/**
 * Writing an anchoring decision onto a panel.
 *
 * Its own module, and not part of `overlay-dom.ts`, for a structural reason rather than a tidy one:
 * that file is a leaf every popup-owning module already depends on, and reaching from it to the
 * catalogue and to `overlay.ts` closes five cycles through `dismissal` and `overlay-branch`. This
 * sits above all three instead — the barrel imports it, and nothing else does.
 */
import { applyOverlayProperties } from "./overlay-dom.js";
import { popupAlignmentClass, popupPlacementClass, } from "./overlay.js";
/**
 * Writes an anchoring decision onto a panel: its coordinates, and the classes that say where it went.
 *
 * Every renderer that places its own popup performs the same four writes after `anchorOverlay`
 * answers — the custom properties, the placement as data, the stale placement classes off, the new
 * ones on — and the order matters in one place: the classes come off before they go on, or a panel
 * that flipped from above to below wears both answers at once and the theme picks whichever the
 * stylesheet mentions last.
 *
 * Written per renderer, this is the shape that drifts. Two of them had already invented
 * `mdy-overlay-panel--above`, a name no stylesheet has ever matched, while the catalogue's own
 * spelling went unemitted — which is the failure `popupPlacementClass` was written to end, repeated
 * one level up in the code that calls it.
 */
/**
 * Every placement and every alignment, as keys rather than a list.
 *
 * A list would compile while missing one, and a placement nobody clears is a class a panel keeps
 * wearing after it has moved. As a total record the compiler refuses the omission the day the
 * vocabulary grows.
 */
const EVERY_PLACEMENT = {
    below: true, above: true, overlay: true,
};
const EVERY_ALIGNMENT = { left: true, right: true };
export function applyAnchoredOverlay(panel, kind, anchoring) {
    applyOverlayProperties(panel, anchoring.properties);
    panel.dataset["placement"] = anchoring.placement;
    // Off before on: a panel that has moved must not wear two answers at once.
    clearAnchoredOverlay(panel, kind);
    const placement = popupPlacementClass(kind, anchoring.placement);
    const alignment = popupAlignmentClass(kind, anchoring.decision.alignment);
    if (placement)
        panel.classList.add(placement);
    if (alignment)
        panel.classList.add(alignment);
}
/**
 * Takes every anchoring state class off a panel, so it says nothing about where it is.
 *
 * The half of {@link applyAnchoredOverlay} a renderer needs on its own when its panel stays in the
 * document while shut: a closed popup is not sitting above anything, and one that keeps the class
 * from its last opening tells a theme it is somewhere it is not. A renderer whose panel is removed
 * or rebuilt on each opening never needs this — which is why it took a renderer that keeps one to
 * notice that the clearing written locally covered placement and forgot alignment.
 */
export function clearAnchoredOverlay(panel, kind) {
    for (const placement of Object.keys(EVERY_PLACEMENT)) {
        const named = popupPlacementClass(kind, placement);
        if (named)
            panel.classList.remove(named);
    }
    for (const alignment of Object.keys(EVERY_ALIGNMENT)) {
        const named = popupAlignmentClass(kind, alignment);
        if (named)
            panel.classList.remove(named);
    }
}
